
import Vue from 'vue';
import Component from "vue-class-component";
import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
import HeadingWave from "@/features/shared/components/HeadingWave.vue";
import {
    CountryLightModel, DocumentAttributesClient,
    factory,
    OrganizationTypeModel,
    ResultError,
    SpecialityLightModel,
    SysAdminOrganizationsClient,
    SysAdminSpecialitiesClient,
    UpsertOrganizationRequest
} from "@/api-client";
import toasts from "@/components/Toasts";
import LabelOptional from "@/features/shared/components/LabelOptional.vue";
import {Prop} from "vue-property-decorator";
import AdministrationDetailviewLayout from "@/features/administration/AdministrationDetailviewLayout.vue";
import {NavigationGuardNext} from "vue-router/types/router";
import ErrorsSummary from "@/components/ErrorsSummary.vue";


class Option {
    constructor(public readonly text: string, public readonly value: boolean) {
    }
}


interface Props {
    model?: UpsertOrganizationRequest;
    organizationTypeOptions: OrganizationTypeModel[];
    preselectedOrganizationTypes?: OrganizationTypeModel[];
    countries: CountryLightModel[];
    specialities?: SpecialityLightModel[];
    preselectedSpecialities?: SpecialityLightModel[];
}

@Component({
    components: {
        SectionPageHeading,
        HeadingWave,
        LabelOptional,
        AdministrationDetailviewLayout,
        ErrorsSummary
    },
    async beforeRouteEnter(to, from, next: NavigationGuardNext<UpsertOrganization>) {
        const props = to.meta!.props as Props;
        try {
            props.organizationTypeOptions = await new SysAdminOrganizationsClient().organizationTypes();
            props.countries = await new DocumentAttributesClient().countries();
            props.specialities = await new SysAdminSpecialitiesClient().all(null);

            if (to.params.id) {
                const organizationToUpdate = await new SysAdminOrganizationsClient().details(to.params.id);

                props.model = new UpsertOrganizationRequest({
                    id: organizationToUpdate.id,
                    nameEst: organizationToUpdate.nameEst,
                    nameEng: organizationToUpdate.nameEng,
                    registerCode: organizationToUpdate.registerCode,
                    types: [],
                    countryLetterCode: organizationToUpdate.countryLetterCode,
                    additionalInfo: organizationToUpdate.additionalInfo,
                    active: organizationToUpdate.active,
                    specialities: []
                });

                props.preselectedOrganizationTypes = organizationToUpdate.types;
                const specialitiesFlat = (organizationToUpdate.specialities || []).map(m => m.value);
                props.preselectedSpecialities = props.specialities.filter(s => specialitiesFlat.includes(s.id));
            }
            next();
        } catch (e) {
            next(e)
        }
    }
})
export default class UpsertOrganization extends Vue implements Props {
    @Prop({ required: false, default: () => factory.create(UpsertOrganizationRequest) })
    model!: UpsertOrganizationRequest;
    @Prop({ required: true })
    organizationTypeOptions!: OrganizationTypeModel[];
    @Prop({ required: false, default: () => [] })
    preselectedOrganizationTypes!: OrganizationTypeModel[];
    @Prop({ required: true })
    countries!: CountryLightModel[];
    @Prop({required: false})
    specialities?: SpecialityLightModel[]
    @Prop({ required: false, default: () => [] })
    preselectedSpecialities!: SpecialityLightModel[];

    selectedOrganizationTypes = this.preselectedOrganizationTypes;
    validationError: ResultError = ResultError.createEmpty();
    selectedSpecialities: SpecialityLightModel[] = this.preselectedSpecialities;

    modelWatch = this.model; // "isEstoniaSelected" is not reactive without explicit watch of the prop "model".

    activeInActiveOptions = [
        new Option(this.$ls.Global_Label_Active, true),
        new Option(this.$ls.Global_Label_InActive, false)
    ]

    initialActive = true;

    get isUpdate(): boolean {
        return !!this.$route.params.id;
    }

    get isEstoniaSelected(){
        return this.modelWatch.countryLetterCode == 'EE';
    }

    async save() {
        this.validationError.clear();
        this.model.types = this.selectedOrganizationTypes.map(x => x.value);
        this.model.specialities = this.selectedSpecialities.map(x => x.id);
        await new SysAdminOrganizationsClient().upsert(this.model)
            .then(
                () => {
                    if (this.isUpdate) {
                        this.$router.push({ name: 'admin-organization', params: { id: this.model.id! } });
                    } else {
                        this.$router.push({ name: 'organizations' });
                    }
                    toasts.success(this.$ls.Administration_Organization_Toast_Success_OrganizationSaved);
                },
                (errorResponse: ResultError) => {
                    this.validationError = errorResponse;
                    toasts.failure(this.$ls.Global_Toast_ActionFailureWithErrorsAbove);
                });
    }

    removeOrganizationType(type: OrganizationTypeModel) {
        this.selectedOrganizationTypes.splice(this.selectedOrganizationTypes.indexOf(type), 1);
    }

    removeSpeciality(type: SpecialityLightModel) {
        this.selectedSpecialities.splice(this.selectedSpecialities.indexOf(type), 1);
    }
}
